import * as React from 'react';
import { useContext, useState } from 'react';
import { AlumnoType, TorneoType } from '../../types/types';
import { Box, Checkbox, Chip, FormControlLabel, Grid, IconButton, LinearProgress } from '@mui/material';
import { UserContext } from '../../contexts/userContext';
import { useGetAlumnos } from '../../hooks/getAlumnos';
import dayjs from 'dayjs';
import { DataGrid, GridColDef, GridToolbar, esES } from '@mui/x-data-grid';
import { useAutoAddOrUpdateTorneo } from '../../hooks/useTorneos';
import { ArrowBack, ArrowBackIosNew, ArrowForward, Edit, EmojiEvents, Event, Paid } from '@mui/icons-material';
import WizardTorneo from './WizardTorneo';
import CustomToolbar from '../CustomToolbar/CustomToolbar';

export interface TorneoDetailProps {
  torneo: TorneoType;
  refetchLista: () => void;
  onClose: () => void;
}

export default function TorneoDetails(props:TorneoDetailProps) {
  const { torneo, refetchLista, onClose } = props;
  const { user } = useContext(UserContext);
  const alumnos = useGetAlumnos(user, false);
  const [ mutableTorneo, setMutableTorneo ] = useState<TorneoType>(torneo);
  const [editarTorneo, setEditarTorneo] = useState(false);

  useAutoAddOrUpdateTorneo({ torneo: mutableTorneo }, user);

  const columnasParticipantes: GridColDef[] = [
    {
      field: 'eliminar',
      headerName: 'Eliminar',
      headerClassName: 'table-headers',
      width: 50,
      renderHeader: () => '',
      renderCell: (params) => (
        <IconButton
          onClick={() => {
            const participantes = mutableTorneo?.participantes?.filter((p) => p !== params.row._id);
            setMutableTorneo((prev) => ({ ...prev, participantes }));
          }}
          color="error"
          style={{
            marginTop: 16,
            marginBottom: 16,
          }}
        >
          <ArrowBack />
        </IconButton>
      ),
    },
    {
      field: 'nombre-apellido',
      headerName: 'Nombre y Apellido',
      headerClassName: 'table-headers',
      flex: 1,
      renderHeader: () => <strong>Apellido y Nombre</strong>,
      valueGetter: (params) =>`${params.row.apellido} ${params.row.name}
      ${params.row.becado ? ' becado' : ''} ${params.row.descuentoHermano ? ' descuento' : ''}`,
      renderCell: (params) => {
        const alumno = params.row as AlumnoType;
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
            padding: 0,
            textTransform: 'capitalize',
          }}>
            {`${params.row.apellido} ${params.row.name}`}
            <div style={{
              alignSelf: 'flex-start',
              textAlign: 'end',
              padding: 0,
              margin: 0,
              width: 'min-content',
              marginBottom: 0,
            }}>
              {alumno.becado && <Chip color="success" size='small' label='Becado' style={{ zoom: 0.8, padding: 2 }}/>}
              {alumno.descuentoHermano && <Chip color="success" size='small' label='Descuento' style={{ zoom: 0.8, padding: 2 }}/>}
            </div>
          </div>
        );
      },
    },
    {
      field: 'categoria-nivel',
      headerName: 'categoria-nivel',
      headerClassName: 'table-headers',
      flex: 1,
      renderHeader: () => (<strong>Categoria y Nivel</strong>),
      renderCell: (params) => (
        <span
          style={{
            whiteSpace: 'pre-wrap',
            textAlign: 'left',
          }}
        >
          {!!params.row.categoria && !!params.row.nivel && `${params.row.categoria} - ${params.row.nivel}`}
        </span>
      ),
    },
    { field: 'pago',
      headerName: 'pago',
      headerClassName: 'table-headers',
      width: 50,
      valueGetter: (params) => dayjs(params.row.fechanac).format('DD/MM/YYYY'),
      renderHeader: () => (<strong>Pagó</strong>),
      renderCell: (params) => (
        <FormControlLabel
          control={
            <Checkbox
              key={`checkbox-pagaron-${params.row._id}`}
              onChange={(event) => {
                let items =  mutableTorneo?.pagaron ?? [];
                if (event.target.checked && !items.includes(params.row._id)) items.push(params.row._id);
                if (!event.target.checked) items = items.filter((i) => i !== params.row._id);
                setMutableTorneo((prev) => ({
                  ...prev,
                  pagaron: items,
                }));
              }}
              checked={mutableTorneo?.pagaron?.includes(params.row._id)}
            />
          }
          label="" />
      ),
    },
  ];

  const columnasAlumnos: GridColDef[] = [
    {
      field: 'nombre-apellido',
      headerName: 'Nombre y Apellido',
      headerClassName: 'table-headers',
      flex: 1,
      renderHeader: () => <strong>Apellido y Nombre</strong>,
      valueGetter: (params) =>`${params.row.apellido} ${params.row.name}
      ${params.row.becado ? ' becado' : ''} ${params.row.descuentoHermano ? ' descuento' : ''}`,
      renderCell: (params) => {
        const alumno = params.row as AlumnoType;
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
            padding: 0,
            textTransform: 'capitalize',
          }}>
            {`${params.row.apellido} ${params.row.name}`}
            <div style={{
              alignSelf: 'flex-start',
              textAlign: 'end',
              padding: 0,
              margin: 0,
              width: 'min-content',
              marginBottom: 0,
            }}>
              {alumno.becado && <Chip color="success" size='small' label='Becado' style={{ zoom: 0.8, padding: 2 }}/>}
              {alumno.descuentoHermano && <Chip color="success" size='small' label='Descuento' style={{ zoom: 0.8, padding: 2 }}/>}
            </div>
          </div>
        );
      },
    },
    {
      field: 'categoria-nivel',
      headerName: 'categoria-nivel',
      headerClassName: 'table-headers',
      flex: 1,
      renderHeader: () => (<strong>Categoria y Nivel</strong>),
      renderCell: (params) => (
        <span
          style={{
            whiteSpace: 'pre-wrap',
            textAlign: 'left',
          }}
        >
          {!!params.row.categoria && !!params.row.nivel && `${params.row.categoria} - ${params.row.nivel}`}
        </span>
      ),
    },
    {
      field: 'agregar',
      headerName: 'agregar',
      headerClassName: 'table-headers',
      width: 50,
      renderHeader: () => '',
      renderCell: (params) => (
        <IconButton
          onClick={() => {
            if (!mutableTorneo?.participantes?.includes(params.row._id)){
              const participantes = [...mutableTorneo?.participantes ?? [], params.row._id];
              if (participantes) setMutableTorneo((prev) => ({ ...prev, participantes }));
            }
          }}
          color="success"
          style={{
            marginTop: 16,
            marginBottom: 16,
          }}
        >
          <ArrowForward />
        </IconButton>
      ),
    },
  ];

  if (alumnos.isLoading || !alumnos.data) return <LinearProgress />;
  const alumnosParticipantes = alumnos?.data?.filter((a) => mutableTorneo?.participantes?.includes(a._id))
    .map((a) => ({ ...a, pago: mutableTorneo?.pagaron?.includes(a._id) ? 'SI' : 'NO' }));

  const alumnosActivos = alumnos?.data?.filter((a) => !a.inactivo && !mutableTorneo?.participantes?.includes(a._id));

  return (
    <>
      <WizardTorneo
        torneo={torneo}
        open={editarTorneo}
        onClose={() => setEditarTorneo(false)}
        setOpen={setEditarTorneo}
        refetchLista={refetchLista}
      />
      <div className="torneo-card">
        <button className="edit-button" style={{ top: 5, left: 5, width: 35 }} onClick={() => {onClose();}}>
          <ArrowBackIosNew />
        </button>
        <div className="torneo-header">
          <h3 className="torneo-title">
            {torneo.nombre}
            <button className="edit-button" onClick={() => setEditarTorneo(true)}>
              <Edit />
            </button>
          </h3>
          <span className="torneo-subtitle">{`${torneo.lugar} - ${torneo.organizador}`}</span>
        </div>
        <div className="torneo-details">
          <span className="torneo-info">
            <Event /><b>Fecha:</b> {torneo.fecha}
          </span>
          <span className="torneo-info">
            <EmojiEvents /><b>Tipo:</b> {torneo.tipo}
          </span>
          <span className="torneo-info">
            <Paid /><b>Costo:</b> {`$${torneo.inscripcion}`}
          </span>
        </div>
      </div>
      <Box sx={{ height: 'calc(100vh - 300px)', width: 1 }}>
        <Grid container style={{ height: '100%' }}>
          <Grid xs={5}>
            <DataGrid
              getRowHeight={() => 'auto'}
              headerHeight={30}
              rows={alumnosActivos}
              columns={columnasAlumnos}
              getRowId={(row) =>  row._id ?? row.dni}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              components={{ Toolbar: (toolbarProps) =>
                <>
                  <div style={{ width: '100%' }}>
                    <h3 className="table-title">Alumnos</h3>
                  </div>
                  <GridToolbar {...toolbarProps}
                  />
                </>,
              }}
              componentsProps={{
                toolbar: {
                  csvOptions: { disableToolbarButton: true },
                  printOptions: { disableToolbarButton: true },
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                  style: { height: 55 },
                },
              }}
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              disableSelectionOnClick
              initialState={{
                sorting: {
                  sortModel: [{ field: 'nombre-apellido', sort: 'asc' }],
                },
              }}
            />
          </Grid>
          <Grid xs={7}>
            <DataGrid
              getRowHeight={() => 'auto'}
              headerHeight={30}
              rows={alumnosParticipantes}
              columns={columnasParticipantes}
              getRowId={(row) =>  row._id ?? row.dni}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              components={{
                Toolbar: (toolbarProps) =>
                  <>
                    <div style={{ width: '100%' }}>
                      <h5 className="table-title">Participantes</h5>
                    </div>
                    <CustomToolbar
                      {...toolbarProps}
                      rows={alumnosParticipantes}
                      title={`Torneo ${torneo.nombre}`}
                      eliminarTodos={() => setMutableTorneo((prev) => ({ ...prev, participantes: [] }))}
                      columnMapping={{
                        name: 'Nombre',
                        apellido: 'Apellido',
                        dni: 'DNI',
                        fechanac: 'Fecha de nacimiento',
                        grupo: 'Grupo',
                        genero: 'Genero',
                        categoria: 'Categoria',
                        nivel:'Nivel',
                        pago: 'Pago',
                      }}
                    />
                  </> }}
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              disableSelectionOnClick
              initialState={{
                sorting: {
                  sortModel: [{ field: 'nombre-apellido', sort: 'asc' }],
                },
              }}
            />
          </Grid>

        </Grid>
      </Box>
    </>
  );
}
